import { useState, useEffect, useMemo, useCallback } from "react";
import { debounce } from "lodash";
import { Popover } from "react-tiny-popover";
import clsx from "clsx";
import styles from "../Conversation.module.scss";
import locale from "../../../constants/locale";
import { useTranslation } from "react-i18next";
import Mentionify from "../../mentionify/Mentionify";

interface SearchResult {
  id: string;
  text: string;
  userId: string;
}

interface ConversationSearchProps {
  messages: any[];
  isMobile?: boolean;
}

const ConversationSearch: React.FC<ConversationSearchProps> = ({
  messages,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleCloseSearch = () => {
    setShowSearch(false);
    clearSearch();
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setHasSearched(false);
  };

  // Clear search results when popover is closed
  useEffect(() => {
    if (!showSearch) {
      clearSearch();
    }
  }, [showSearch]);

  const scrollToMessage = (messageId: string) => {
    const messageElement = document.querySelector(`#enda-message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth" });

      // Highlight the message temporarily
      messageElement.classList.add(styles.highlightedMessage);
      setTimeout(() => {
        messageElement.classList.remove(styles.highlightedMessage);
      }, 2000);
    }
  };

  // Search function that takes the query as a parameter
  const searchMessages = useCallback(
    (query: string) => {
      console.log("searching", query, messages.length);
      if (!query.trim() || !messages.length) {
        setSearchResults([]);
        setHasSearched(false);
        return;
      }

      setIsSearching(true);
      setHasSearched(true);

      const lowerQuery = query.toLowerCase();
      const results: SearchResult[] = [];

      messages.forEach((message) => {
        // Search in text messages
        if (
          message.message.type === "text" &&
          message.message.text &&
          message.message.text.toLowerCase().includes(lowerQuery)
        ) {
          results.push({
            id: message.id!,
            text: message.message.text,
            userId: message.userId,
          });
        }
      });

      setSearchResults(results);
      setIsSearching(false);
    },
    [messages]
  );

  // Create a debounced version of the search function
  const debouncedSearch = useMemo(
    () => debounce((query: string) => searchMessages(query), 300),
    [searchMessages]
  );

  // Trigger search on query change
  useEffect(() => {
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery); // Pass the current searchQuery value
    } else {
      setSearchResults([]);
      setHasSearched(false);
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  const handleSelectResult = (messageId: string) => {
    scrollToMessage(messageId);
    handleCloseSearch();
  };

  const searchContent = (
    <div className={styles.searchContainer}>
      <div className="field">
        <div className="control is-flex">
          <input
            className="input"
            type="search"
            placeholder={t(locale.searchInConversation)}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                searchMessages(searchQuery);
              }
            }}
            autoFocus
          />
        </div>
      </div>
      {hasSearched && (
        <div className={styles.searchResults}>
          {isSearching ? (
            <p className="has-text-centered py-2">Searching...</p>
          ) : searchResults.length > 0 ? (
            <>
              <p className="subtitle is-6 has-text-weight-semibold mb-2">
                {t(locale.foundMessages, { count: searchResults.length })}
              </p>
              <div className={styles.searchResultsList}>
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className={styles.searchResultItem}
                    onClick={() => handleSelectResult(result.id)}
                  >
                    <div className="line-clamp-2">
                      <Mentionify>{result.text}</Mentionify>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="has-text-centered py-2 has-text-grey">
              {t(locale.noMessagesFound)}
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      isOpen={showSearch}
      padding={10}
      positions={["bottom", "left"]}
      onClickOutside={handleCloseSearch}
      containerClassName={styles.searchPopover}
      content={searchContent}
    >
      <button
        className={clsx(styles.noBtn, {
          "has-text-primary": showSearch,
        })}
        onClick={() => setShowSearch(!showSearch)}
      >
        <span className="icon">
          <img src="/assets/search.svg" alt="Search" width={18} />
        </span>
      </button>
    </Popover>
  );
};

export default ConversationSearch;
